/**
 * Landers Statistics Tab
 * Uses shared CampaignStatsDashboard with landers-specific data from Voluum.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useMemo } from 'react';

import { useAuth, useFlowOptimizerTags, useLanderPerformanceTags, useLandersWithStats } from '@/hooks';
import { statsTrackerId } from '@/features/trackers';

import {
  CampaignStatsDashboard,
  TruncatedFlowItemCell,
  FILTER_FIELDS_LANDERS,
  GROUP_BY_OPTIONS_LANDERS,
  toRangeISO,
  toGenericStatsRowFromLander,
  useStatsTabState,
  useStatsCompareState,
  useFilteredAndSortedRowsGeneric,
  type GroupByKey,
  type GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';

interface LandersStatsTabProps {
  countryKey: string;
  trafficCampaignId?: string | null;
  trafficProvider?: string | null;
  wiserIds: number[];
  workDate: string;
}

export const LandersStatsTab: FC<LandersStatsTabProps> = ({
  countryKey,
  trafficCampaignId,
  trafficProvider,
  wiserIds,
  workDate,
}) => {
  const { user } = useAuth();
  const state = useStatsTabState(workDate, 'landers');
  const range = useMemo(
    () => toRangeISO(state.fromDate, state.toDate),
    [state.fromDate, state.toDate],
  );

  const perfTagsQuery = useLanderPerformanceTags(
    countryKey,
    wiserIds.length > 0 ? wiserIds : undefined,
    { from: range.from, to: range.to, tz: 'Europe/Paris', currency: state.currency },
  );
  const tagsByLanderId = perfTagsQuery.data?.tagsByLanderId ?? {};
  const tagDetailsByLanderId = perfTagsQuery.data?.tagDetailsByLanderId ?? {};
  const tagColors = perfTagsQuery.data?.tagColors;

  const flowTagsQuery = useFlowOptimizerTags(
    countryKey,
    trafficCampaignId ?? undefined,
    statsTrackerId(trafficProvider),
    'landers',
    Boolean(trafficCampaignId),
  );
  const flowTagsByLanderId = flowTagsQuery.data?.tagsByItemId ?? {};
  const flowTagColors = flowTagsQuery.data?.tagColors;

  const query = useLandersWithStats({
    countryKey,
    trafficCampaignId: trafficCampaignId ?? undefined,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    limit: state.length,
  });

  const compare = useStatsCompareState(state.fromDate, state.toDate, state.setDateRange);
  const compareRange = useMemo(
    () => toRangeISO(compare.compareFrom, compare.compareTo),
    [compare.compareFrom, compare.compareTo],
  );
  const compareQuery = useLandersWithStats({
    countryKey,
    trafficCampaignId: trafficCampaignId ?? undefined,
    wiserIds,
    from: compareRange.from,
    to: compareRange.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    limit: state.length,
    enabled: compare.compareMode,
  });
  const compareById = useMemo(() => {
    const map = new Map<string, Record<string, number> | null>();
    (compareQuery.data?.rows ?? []).forEach((r, i) => {
      const g = toGenericStatsRowFromLander(r, i);
      map.set(g.id, g.voluum);
    });
    return map;
  }, [compareQuery.data?.rows]);

  const allLanderRows = query.data?.rows ?? [];
  const allGenericRows = useMemo(
    () =>
      allLanderRows.map((r, i) => {
        const base = toGenericStatsRowFromLander(r, i);
        const landerId = r.landerId ? String(r.landerId).trim() : '';
        const tags =
          landerId
            ? (tagsByLanderId[landerId] ??
              Object.entries(tagsByLanderId).find(
                ([k]) => k.toLowerCase() === landerId.toLowerCase(),
              )?.[1]) ?? []
            : [];
        return { ...base, performanceTags: tags };
      }),
    [allLanderRows, tagsByLanderId],
  );

  const { rows, groups } = useFilteredAndSortedRowsGeneric(
    allGenericRows,
    state.filtersState,
    state.sort,
    workDate,
    state.groupBy,
  );

  const totals = query.data?.totals ?? {};

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const name of Object.keys(tagColors ?? {})) if (name.trim()) set.add(name.trim());
    for (const tags of Object.values(tagsByLanderId)) {
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagColors, tagsByLanderId]);

  const tagDetailsLookup = useCallback(
    (landerId: string) => {
      if (!landerId) return undefined;
      const direct = tagDetailsByLanderId[landerId];
      if (direct) return direct;
      const entry = Object.entries(tagDetailsByLanderId).find(
        ([k]) => k.toLowerCase() === landerId.toLowerCase(),
      );
      return entry?.[1];
    },
    [tagDetailsByLanderId],
  );

  const renderFirstCell = useCallback(
    (row: GenericStatsRow) => {
      const landerId = row.id;
      const performanceTags = landerId
        ? (tagsByLanderId[landerId] ??
          Object.entries(tagsByLanderId).find(
            ([k]) => k.toLowerCase() === landerId.toLowerCase(),
          )?.[1])
        : undefined;
      const performanceTagDetails = landerId ? tagDetailsLookup(landerId) : undefined;
      return (
        <TruncatedFlowItemCell
          itemId={landerId}
          label={row.label ?? '—'}
          itemType="lander"
          performanceTags={performanceTags}
          performanceTagDetails={performanceTagDetails}
          tagColors={tagColors}
          flowTags={landerId ? flowTagsByLanderId[landerId] : undefined}
          flowTagColors={flowTagColors}
        />
      );
    },
    [tagsByLanderId, tagDetailsLookup, tagColors, flowTagsByLanderId, flowTagColors],
  );

  return (
    <CampaignStatsDashboard
      tab="landers"
      title="Landers"
      emptyMessage="No landers found for this range."
      loadingTitle="Loading lander performance data"
      loadingDesc="Fetching tracker metrics grouped by lander."
      errorPrefix="Failed to load lander stats."
      firstColumnLabel="Lander"
      firstColumnSortKey="lander"
      rowUnitLabel="lander"
      filterFields={FILTER_FIELDS_LANDERS}
      groupByOptions={GROUP_BY_OPTIONS_LANDERS}
      rows={rows}
      totals={totals}
      groups={groups}
      totalRowCount={query.data?.total ?? allGenericRows.length}
      allRowsLength={allGenericRows.length}
      currency={query.data?.currency ?? 'USD'}
      fromDate={state.fromDate}
      toDate={state.toDate}
      onFromChange={state.setFromDate}
      onToChange={state.setToDate}
      onDateRangeChange={state.setDateRange}
      length={state.length}
      onLengthChange={state.setLength}
      filtersState={state.filtersState}
      setFilters={state.setFilters}
      defaultFilter={state.defaultFilter}
      groupBy={state.groupBy}
      setGroupBy={(v) => state.setGroupBy(v as GroupByKey)}
      collapsedGroups={state.collapsedGroups}
      setCollapsedGroups={state.setCollapsedGroups}
      toggleGroup={state.toggleGroup}
      sort={state.sort}
      onSortClick={state.handleSortClick}
      selectedRows={state.selectedRows}
      setSelectedRows={state.setSelectedRows}
      renderFirstCell={renderFirstCell}
      isError={query.isError ?? false}
      error={query.error}
      isLoading={query.isLoading ?? false}
      isFetching={query.isFetching ?? false}
      dataUpdatedAt={query.dataUpdatedAt}
      refetch={() => query.refetch()}
      showLimitSelect
      showGroupBySelect={false}
      firstColumnClassName="min-w-[280px] md:min-w-[480px]"
      csvExportUserId={user?.id}
      compareCapable
      compareMode={compare.compareMode}
      onToggleCompare={compare.toggleCompareMode}
      compareFromDate={compare.compareFrom}
      compareToDate={compare.compareTo}
      onCompareFromChange={compare.setCompareFrom}
      onCompareToChange={compare.setCompareTo}
      onCompareRangeChange={compare.setCompareRange}
      compareById={compareById}
      compareTotals={compareQuery.data?.totals ?? {}}
      onComparePreset={compare.applyPreset}
      granularity={compare.granularity}
      onGranularityChange={compare.setGranularity}
      countryKey={countryKey}
      wiserIds={wiserIds}
      tagFilterOptions={availableTags}
      tagFilterColors={tagColors}
      tagsLoading={perfTagsQuery.isLoading}
    />
  );
};

export default LandersStatsTab;
